import { useEffect, useMemo, useState } from 'react'
import { onValue, ref } from 'firebase/database'

import { db, tref } from '../lib/firebase.js'
import TvLeaderboard from '../leaderboard/TvLeaderboard.jsx'
import CLUB from '../config/club.js'

// Auto-discovering test leaderboard. Subscribes to /tournaments, picks the
// most-recently-created tournament tagged isTestStation + testStationTile
// === 'scorecard', and renders the real TV leaderboard component pinned to
// it — same look, fonts, auto-scroll, and live updates as the production
// /tv/scores display.
//
// Multiple TVs/iPads can all open /testleaderboard at the same time — every
// instance independently picks the same active test tournament and follows
// score writes in real time. No URL param, no setup; opens before or after
// Scorecard Test is created and just starts working.
export default function TestLeaderboard() {
  const [tournaments, setTournaments] = useState({})

  useEffect(() => {
    const off = onValue(tref('tournaments'), (snap) => setTournaments(snap.val() || {}))
    return () => off()
  }, [])

  const activeId = useMemo(() => {
    const entries = Object.entries(tournaments).filter(
      ([, v]) => v?.info?.isTestStation && v?.info?.testStationTile === 'scorecard',
    )
    if (entries.length === 0) return null
    entries.sort(
      (a, b) =>
        (b[1]?.info?.testStationCreatedAt || b[1]?.info?.createdAt || 0) -
        (a[1]?.info?.testStationCreatedAt || a[1]?.info?.createdAt || 0),
    )
    return entries[0][0]
  }, [tournaments])

  if (!activeId) {
    return (
      <div
        style={{
          minHeight: '100vh', backgroundColor: CLUB.backgroundColor || 'var(--ge-primary)',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 28, textAlign: 'center',
        }}>
        <div style={{ maxWidth: 520 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: CLUB.accentColor, fontWeight: 800 }}>
            🧪 TEST LEADERBOARD
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 900, margin: '8px 0' }}>Waiting for Scorecard Test</h1>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.55 }}>
            No active scorecard test was found. Open <strong>/teststation</strong> on
            the admin browser and tap <strong>Scorecard Test → Single Team</strong> or{' '}
            <strong>Multiple Teams</strong>. Scores entered on those phones will
            stream onto this screen automatically.
          </p>
        </div>
      </div>
    )
  }

  // Same hardening pattern as /testphotowall: wrap in an explicitly
  // viewport-sized container (100vw × 100vh) so TvLeaderboard's internal
  // min-h-full / h-full chain always resolves against a definitely-sized
  // parent. Inset-only sizing (position: fixed; inset: 0) silently
  // collapses % heights on some TV browsers; vw/vh is rock-solid.
  return (
    <div style={{ width: '100vw', height: '100vh', backgroundColor: 'var(--ge-primary)', overflow: 'hidden' }}>
      <TvLeaderboard tournamentIdProp={activeId} />
    </div>
  )
}
